import React from 'react';
import { TranscriptEntry } from '../../types';
import { AvatarSuggestion, FacilitatorGuidance as FacilitatorGuidanceData } from '../../services/workshop';
import TranscriptDrainControl from './TranscriptDrainControl';
import { ResizableGrid, ResizableRow, useDashboardLayout } from './ResizableGrid';

interface Phase {
  id: string;
  name: string;
}

interface DashboardViewProps {
  // Session state
  currentPhase: Phase;
  responseMode: string;
  onResponseModeChange: (mode: string) => void;
  autoRespondEnabled: boolean;
  onToggleAutoRespond: (enabled: boolean) => void;
  loadingAutoRespondState: boolean;
  sessionMode: 'active' | 'readOnly';
  userAccessLevel: 'owner' | 'read' | 'write';

  // Input
  userInput: string;
  onUserInputChange: (value: string) => void;
  onSend: () => void;
  isLoading: boolean;

  // Speech
  isListeningContinuous: boolean;
  isSupported: boolean;
  onStartContinuous: () => void;
  onStopContinuous: () => void;

  // Data
  transcript: TranscriptEntry[];
  pendingContributions: AvatarSuggestion[];
  sessionAvatars: any[];
  speakingAvatar: string | null;
  facilitatorGuidance: FacilitatorGuidanceData | null;
  guidanceLoading: boolean;
  onRefreshGuidance: () => void;
  onCopyPrompt: (prompt: string) => void;

  // Contribution actions
  onApprove: (suggestion: AvatarSuggestion) => void;
  onIgnore: (index: number) => void;
  onReject: (index: number, reason?: string) => void;

  // Navigation
  onNavigateTo: (view: string) => void;
  sessionId?: string;
  canEditDrain?: boolean;
}

const RESPONSE_MODES = [
  { value: 'open_ideas', label: 'Open Brainstorming' },
  { value: 'structured_brainstorm', label: 'Structured' },
  { value: 'solution_focused', label: 'Solution Focused' },
  { value: 'closing_down', label: 'Closing Down' },
  { value: 'next_steps', label: 'Next Steps' },
  { value: 'critique', label: 'Critique' },
  { value: 'silent', label: 'Silent' },
  { value: 'blank', label: 'Blank (No Response)' },
];

function isInactiveContribution(c: AvatarSuggestion): boolean {
  return c.state === 'accepted' || c.state === 'ignored' || c.state === 'rejected';
}

function getAvatarStatus(avatar: any, speakingAvatar: string | null, pendingContributions: AvatarSuggestion[]) {
  if (speakingAvatar === avatar.role) return { label: 'Speaking', color: 'bg-green-500', textColor: 'text-green-600' };
  const hasPending = pendingContributions.some(
    (c) => c.role === avatar.role && !isInactiveContribution(c)
  );
  if (hasPending) return { label: 'Thinking', color: 'bg-yellow-500', textColor: 'text-yellow-600' };
  return { label: 'Idle', color: 'bg-gray-400', textColor: 'text-gray-500' };
}

function MiniAvatarIcon({ entry, avatar }: { entry: TranscriptEntry; avatar: any }) {
  if (entry.speaker === 'human') {
    return <div className="size-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 border border-blue-200 text-[10px] font-bold">H</div>;
  }
  if (entry.speaker === 'whiteboard') {
    return <div className="size-6 rounded-full bg-purple-100 flex items-center justify-center border border-purple-200 text-xs">🖼️</div>;
  }
  if (avatar?.imageUrl) {
    return <div className="size-6 rounded-full bg-center bg-cover border" style={{ backgroundImage: `url("${avatar.imageUrl}")`, borderColor: avatar.primaryColor || '#818cf8' }} />;
  }
  return (
    <div className="size-6 rounded-full flex items-center justify-center border text-[10px]" style={{ backgroundColor: avatar?.secondaryColor || '#e0e7ff', borderColor: avatar?.primaryColor || '#818cf8', color: avatar?.primaryColor || '#4f46e5' }}>
      {avatar?.emoji || '🤖'}
    </div>
  );
}

interface SessionStatusCardProps {
  currentPhase: Phase;
  responseMode: string;
  onResponseModeChange: (mode: string) => void;
  isReadOnly: boolean;
}

function SessionStatusCard({ currentPhase, responseMode, onResponseModeChange, isReadOnly }: SessionStatusCardProps) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 flex flex-col justify-between h-full relative overflow-hidden">
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Current Phase</h3>
          <span className="bg-primary/10 text-primary text-xs font-bold px-2 py-1 rounded-full">Active</span>
        </div>
        <p className="text-3xl font-bold text-slate-800 capitalize">{currentPhase.name}</p>
      </div>
      <div className="mt-4">
        <label className="block text-xs font-medium text-slate-600 mb-1.5">Response Mode</label>
        <select
          value={responseMode}
          onChange={(e) => onResponseModeChange(e.target.value)}
          className="w-full appearance-none bg-slate-50 border border-slate-200 text-slate-700 text-sm rounded-lg focus:ring-primary focus:border-primary block p-2.5 pr-8"
          disabled={isReadOnly}
        >
          {RESPONSE_MODES.map((mode) => (
            <option key={mode.value} value={mode.value}>{mode.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

interface AvatarActivityCardProps {
  sessionAvatars: any[];
  speakingAvatar: string | null;
  pendingContributions: AvatarSuggestion[];
  onNavigateTo: (view: string) => void;
}

function AvatarActivityCard({ sessionAvatars, speakingAvatar, pendingContributions, onNavigateTo }: AvatarActivityCardProps) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Avatar Activity</h3>
        <button
          onClick={() => onNavigateTo('avatars')}
          className="text-primary text-xs font-medium hover:underline"
        >
          View All
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 flex-1 overflow-hidden">
        {sessionAvatars.slice(0, 4).map((avatar) => {
          const status = getAvatarStatus(avatar, speakingAvatar, pendingContributions);
          return (
            <div key={avatar.role} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50 border border-slate-100">
              <div className="relative">
                {avatar.imageUrl ? (
                  <div
                    className="size-8 rounded-full bg-center bg-no-repeat bg-cover border-2 shadow-sm"
                    style={{ backgroundImage: `url("${avatar.imageUrl}")`, borderColor: avatar.primaryColor || '#818cf8' }}
                  />
                ) : (
                  <div
                    className="size-8 rounded-full flex items-center justify-center border-2 shadow-sm text-sm"
                    style={{ backgroundColor: avatar.secondaryColor || '#e0e7ff', borderColor: avatar.primaryColor || '#818cf8', color: avatar.primaryColor || '#4f46e5' }}
                  >
                    {avatar.emoji || '🤖'}
                  </div>
                )}
                <span className={`absolute -bottom-1 -right-1 size-2.5 ${status.color} border-2 border-white rounded-full`} />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs font-bold truncate" style={{ color: avatar.primaryColor || '#334155' }}>{avatar.name || avatar.role}</span>
                <span className={`text-[10px] truncate ${status.textColor}`}>{status.label}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface EngagementCardProps {
  engagementScore: number;
  totalContributions: number;
  acceptedCount: number;
}

function EngagementCard({ engagementScore, totalContributions, acceptedCount }: EngagementCardProps) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 h-full flex flex-col">
      <h3 className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-4">Engagement</h3>
      <div className="flex items-center justify-between flex-1">
        <div className="relative size-24 shrink-0">
          <svg className="size-full -rotate-90" viewBox="0 0 36 36">
            <path
              className="text-slate-100"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="text-primary"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke="currentColor"
              strokeDasharray={`${engagementScore}, 100`}
              strokeWidth="4"
            />
          </svg>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
            <span className="text-xl font-bold text-slate-800">{engagementScore}%</span>
          </div>
        </div>
        <div className="flex flex-col gap-3 flex-1 pl-6">
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-500">Contributions</span>
              <span className="font-bold text-slate-700">{totalContributions}</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-1.5">
              <div className="bg-primary h-1.5 rounded-full" style={{ width: `${Math.min(100, totalContributions * 5)}%` }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-500">Accepted</span>
              <span className="font-bold text-green-600">
                {totalContributions > 0 ? Math.round((acceptedCount / totalContributions) * 100) : 0}%
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-1.5">
              <div
                className="bg-green-500 h-1.5 rounded-full"
                style={{ width: `${totalContributions > 0 ? (acceptedCount / totalContributions) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface FacilitatorInputCardProps {
  sessionMode: 'active' | 'readOnly';
  userAccessLevel: 'owner' | 'read' | 'write';
  isReadOnly: boolean;
  loadingAutoRespondState: boolean;
  isLoading: boolean;
  autoRespondEnabled: boolean;
  onToggleAutoRespond: (enabled: boolean) => void;
  userInput: string;
  onUserInputChange: (value: string) => void;
  onSend: () => void;
  isListeningContinuous: boolean;
  isSupported: boolean;
  onStartContinuous: () => void;
  onStopContinuous: () => void;
}

function FacilitatorInputCard({
  sessionMode,
  userAccessLevel,
  isReadOnly,
  loadingAutoRespondState,
  isLoading,
  autoRespondEnabled,
  onToggleAutoRespond,
  userInput,
  onUserInputChange,
  onSend,
  isListeningContinuous,
  isSupported,
  onStartContinuous,
  onStopContinuous,
}: FacilitatorInputCardProps) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-slate-800 text-lg font-bold">Facilitator Input</h3>
        <div className="flex items-center gap-3">
          {/* Auto-respond toggle */}
          {sessionMode === 'active' && userAccessLevel !== 'read' && (
            <div
              className={`flex items-center gap-2 cursor-pointer select-none ${loadingAutoRespondState || isLoading ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={() => onToggleAutoRespond(!autoRespondEnabled)}
            >
              <span className="text-xs font-medium text-slate-600">Auto-respond</span>
              <div
                className={`w-9 h-5 rounded-full transition-colors ${
                  autoRespondEnabled ? 'bg-primary' : 'bg-slate-300'
                }`}
              >
                <div
                  className={`w-4 h-4 bg-white rounded-full shadow transition-transform mt-0.5 ${
                    autoRespondEnabled ? 'translate-x-[18px]' : 'translate-x-0.5'
                  }`}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 relative mb-4">
        <textarea
          value={userInput}
          onChange={(e) => onUserInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          disabled={isListeningContinuous || isReadOnly}
          className="w-full h-32 p-4 rounded-xl bg-slate-50 border border-slate-200 focus:ring-primary focus:border-primary resize-none text-slate-700 text-sm disabled:opacity-50"
          placeholder="Type instructions, questions, or prompts for the avatars and participants..."
        />
      </div>
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>Press Enter to send</span>
        </div>
        <div className="flex items-center gap-2">
          {isSupported && sessionMode === 'active' && userAccessLevel !== 'read' && (
            <button
              onClick={isListeningContinuous ? onStopContinuous : onStartContinuous}
              className={`px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all ${
                isListeningContinuous
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200'
              }`}
            >
              {isListeningContinuous ? '⏹ Stop' : '🎤 Continuous'}
            </button>
          )}
          <button
            onClick={onSend}
            disabled={isLoading || !userInput.trim() || isListeningContinuous || isReadOnly}
            className="bg-primary hover:bg-primary-dark text-white px-6 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all shadow-sm shadow-primary/30 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>Send to Avatars</span>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

interface LiveTranscriptCardProps {
  transcript: TranscriptEntry[];
  sessionAvatars: any[];
  onNavigateTo: (view: string) => void;
  sessionId?: string;
  canEditDrain?: boolean;
}

function LiveTranscriptCard({ transcript, sessionAvatars, onNavigateTo, sessionId, canEditDrain }: LiveTranscriptCardProps) {
  return (
    <div className="bg-white rounded-xl p-0 shadow-sm border border-slate-100 flex flex-col overflow-hidden h-full">
      <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
        <h3 className="text-slate-800 text-sm font-bold">Live Transcript</h3>
        <div className="flex items-center gap-3">
          {sessionId && <TranscriptDrainControl sessionId={sessionId} canEdit={!!canEditDrain} />}
          <span className="flex h-2 w-2 relative">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
          </span>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {transcript.length === 0 ? (
          <div className="text-sm text-slate-400 text-center py-8">No conversation yet...</div>
        ) : (
          transcript.slice(-5).map((entry) => {
            const avatar = entry.speaker === 'avatar'
              ? sessionAvatars.find((a: any) => a.role === entry.avatarRole)
              : undefined;
            const speakerName = entry.speaker === 'human'
              ? 'Facilitator'
              : avatar?.name || entry.avatarRole || entry.speaker;

            return (
              <div key={entry.id ?? `${entry.speaker}-${entry.timestamp}`} className="flex gap-2.5">
                {/* Mini avatar circle */}
                <div className="shrink-0 mt-0.5">
                  <MiniAvatarIcon entry={entry} avatar={avatar} />
                </div>
                {/* Content */}
                <div className="min-w-0 flex-1">
                  <span className="text-xs font-bold mr-1.5" style={entry.speaker === 'avatar' && avatar?.primaryColor ? { color: avatar.primaryColor } : undefined}>
                    {speakerName}
                  </span>
                  <span className="text-xs text-slate-600">
                    {entry.text.length > 100 ? entry.text.slice(0, 100) + '...' : entry.text}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
      <button
        onClick={() => onNavigateTo('transcript')}
        className="px-5 py-3 border-t border-slate-100 text-xs font-medium text-primary hover:bg-slate-50 transition-colors text-center"
      >
        View Full Transcript →
      </button>
    </div>
  );
}

const DashboardView: React.FC<DashboardViewProps> = ({
  currentPhase,
  responseMode,
  onResponseModeChange,
  autoRespondEnabled,
  onToggleAutoRespond,
  loadingAutoRespondState,
  sessionMode,
  userAccessLevel,
  userInput,
  onUserInputChange,
  onSend,
  isLoading,
  isListeningContinuous,
  isSupported,
  onStartContinuous,
  onStopContinuous,
  transcript,
  pendingContributions,
  sessionAvatars,
  speakingAvatar,
  facilitatorGuidance,
  guidanceLoading,
  onRefreshGuidance,
  onCopyPrompt,
  onApprove,
  onIgnore,
  onReject,
  onNavigateTo,
  sessionId,
}) => {
  const isReadOnly = sessionMode === 'readOnly' || userAccessLevel === 'read';
  const pending = pendingContributions.filter((c) => !isInactiveContribution(c));
  const pendingCount = pending.length;

  const engagementScore = transcript.length > 0
    ? Math.min(100, Math.round((transcript.length / Math.max(sessionAvatars.length * 3, 1)) * 100))
    : 0;

  const acceptedCount = pendingContributions.filter((c) => c.state === 'accepted').length;
  const totalContributions = pendingContributions.length;

  const { layout, updateRowSizes, updateRow1Cols, updateRow2Cols, updateRow3Cols, resetLayout } = useDashboardLayout();

  return (
    <div className="flex-1 overflow-hidden bg-surface-light p-6 flex flex-col">
      {/* Reset layout button */}
      <div className="flex justify-end mb-2 shrink-0">
        <button
          onClick={resetLayout}
          className="text-[11px] text-slate-400 hover:text-primary flex items-center gap-1 transition-colors"
          title="Reset panel sizes to defaults"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Reset Layout
        </button>
      </div>
      <ResizableGrid rowSizes={layout.rowSizes} onRowSizesChange={updateRowSizes} minRowHeight={150}>
        {/* Row 1: Status, Avatars, Metrics */}
        <ResizableRow columnSizes={layout.row1Cols} onColumnSizesChange={updateRow1Cols} minColumnWidth={180}>
          <SessionStatusCard
            currentPhase={currentPhase}
            responseMode={responseMode}
            onResponseModeChange={onResponseModeChange}
            isReadOnly={isReadOnly}
          />
          <AvatarActivityCard
            sessionAvatars={sessionAvatars}
            speakingAvatar={speakingAvatar}
            pendingContributions={pendingContributions}
            onNavigateTo={onNavigateTo}
          />
          <EngagementCard
            engagementScore={engagementScore}
            totalContributions={totalContributions}
            acceptedCount={acceptedCount}
          />
        </ResizableRow>

        {/* Row 2: Input & Live Transcript */}
        <ResizableRow columnSizes={layout.row2Cols} onColumnSizesChange={updateRow2Cols} minColumnWidth={200}>
          <FacilitatorInputCard
            sessionMode={sessionMode}
            userAccessLevel={userAccessLevel}
            isReadOnly={isReadOnly}
            loadingAutoRespondState={loadingAutoRespondState}
            isLoading={isLoading}
            autoRespondEnabled={autoRespondEnabled}
            onToggleAutoRespond={onToggleAutoRespond}
            userInput={userInput}
            onUserInputChange={onUserInputChange}
            onSend={onSend}
            isListeningContinuous={isListeningContinuous}
            isSupported={isSupported}
            onStartContinuous={onStartContinuous}
            onStopContinuous={onStopContinuous}
          />
          <LiveTranscriptCard
            transcript={transcript}
            sessionAvatars={sessionAvatars}
            onNavigateTo={onNavigateTo}
            sessionId={sessionId}
            canEditDrain={sessionMode === 'active' && userAccessLevel !== 'read'}
          />
        </ResizableRow>

        {/* Row 3: Guidance & Contributions */}
        <ResizableRow columnSizes={layout.row3Cols} onColumnSizesChange={updateRow3Cols} minColumnWidth={200}>
          {/* AI Guidance */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 h-full overflow-y-auto">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-slate-800 text-sm font-bold">AI Guidance</h3>
                <p className="text-xs text-slate-500">Real-time suggestions for session success</p>
              </div>
              <button
                onClick={onRefreshGuidance}
                disabled={guidanceLoading}
                className="ml-auto text-xs text-primary hover:underline disabled:opacity-50"
              >
                {guidanceLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
            <div className="space-y-3">
              {facilitatorGuidance ? (
                <>
                  {facilitatorGuidance.summary && (
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                      <p className="text-sm font-medium text-slate-700">{facilitatorGuidance.summary}</p>
                    </div>
                  )}
                  {facilitatorGuidance.suggestedPrompts?.slice(0, 2).map((prompt) => (
                    <div key={prompt} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                      <div className="mt-0.5">
                        <div className="size-4 rounded-full bg-primary/20 flex items-center justify-center">
                          <div className="size-1.5 rounded-full bg-primary" />
                        </div>
                      </div>
                      <p className="text-sm text-slate-700">{prompt}</p>
                    </div>
                  ))}
                  {facilitatorGuidance.missingPerspectives && facilitatorGuidance.missingPerspectives.length > 0 && (
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                      <p className="text-xs font-semibold text-amber-700 mb-1">Missing Perspectives</p>
                      <p className="text-sm text-amber-600">
                        {facilitatorGuidance.missingPerspectives.map((p) => p.avatar + ': ' + p.reason).join(', ')}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-slate-400 text-center py-4">
                  {guidanceLoading ? 'Loading guidance...' : 'Guidance will appear after conversation starts'}
                </div>
              )}
            </div>
          </div>

          {/* Pending Contributions */}
          <div data-training-id="pending-contributions-card" className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 h-full overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg text-orange-600">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-slate-800 text-sm font-bold">Pending Contributions</h3>
                  <p className="text-xs text-slate-500">Avatar ideas needing review</p>
                </div>
              </div>
              {pendingCount > 0 && (
                <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2 py-1 rounded-full">
                  {pendingCount} New
                </span>
              )}
            </div>
            <div className="space-y-3">
              {pending
                .slice(0, 3)
                .map((contribution, idx) => {
                  const originalIndex = pendingContributions.indexOf(contribution);
                  const avatar = sessionAvatars.find((a: any) => a.role === contribution.role);
                  return (
                    <div
                      key={contribution.role}
                      className="flex items-center justify-between p-3 border border-slate-200 rounded-xl hover:shadow-md transition-shadow bg-white relative overflow-hidden"
                    >
                      {/* Left accent bar in avatar color */}
                      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: avatar?.primaryColor || (contribution.urgency === 'urgent' ? '#00c7c4' : '#60a5fa') }} />

                      <div className="flex items-center gap-3 min-w-0 flex-1 pl-2">
                        {/* Mini avatar circle */}
                        {avatar?.imageUrl ? (
                          <div className="size-7 rounded-full bg-center bg-cover border shrink-0" style={{ backgroundImage: `url("${avatar.imageUrl}")`, borderColor: avatar.primaryColor || '#818cf8' }} />
                        ) : (
                          <div className="size-7 rounded-full flex items-center justify-center border shrink-0 text-xs" style={{ backgroundColor: avatar?.secondaryColor || '#e0e7ff', borderColor: avatar?.primaryColor || '#818cf8', color: avatar?.primaryColor || '#4f46e5' }}>
                            {avatar?.emoji || '🤖'}
                          </div>
                        )}
                        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold" style={{ color: avatar?.primaryColor || '#4f46e5' }}>
                              {contribution.name || avatar?.name || contribution.role}
                            </span>
                            {contribution.urgency === 'urgent' && (
                              <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 text-[10px] px-1.5 py-0.5 rounded-full font-bold border border-red-100">
                                <span className="size-1.5 rounded-full bg-red-600 animate-pulse" />Urgent
                              </span>
                            )}
                            {contribution.urgency === 'important' && (
                              <span className="bg-amber-50 text-amber-700 text-[10px] px-1.5 py-0.5 rounded-full font-bold border border-amber-100">
                                Important
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-700 font-medium truncate">
                            "{contribution.response.length > 80 ? contribution.response.slice(0, 80) + '...' : contribution.response}"
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-3 shrink-0">
                        <button
                          onClick={() => onIgnore(originalIndex)}
                          className="size-8 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                        <button
                          onClick={() => onApprove(contribution)}
                          className="size-8 flex items-center justify-center rounded-lg bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              {pendingCount === 0 && (
                <div className="text-sm text-slate-400 text-center py-4">No pending contributions</div>
              )}
              {pendingCount > 3 && (
                <button
                  onClick={() => onNavigateTo('contributions')}
                  className="w-full text-xs font-medium text-primary hover:underline text-center py-2"
                >
                  View All {pendingCount} Contributions →
                </button>
              )}
            </div>
          </div>
        </ResizableRow>
      </ResizableGrid>
    </div>
  );
};

export default DashboardView;
